import { useEffect, useState } from 'react';
import type { ObjectId } from '@/types';
import { useCameraStore } from '@/store/cameraStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { cullVisible } from '@/core/rendering/culling';
import { useEngine } from '@/app/EngineContext';

const sameSet = (a: readonly ObjectId[], b: readonly ObjectId[]): boolean => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
};

/**
 * The set of object ids that should be mounted. Recomputed at most once per frame
 * when the camera or document changes, and only triggers a React update when the
 * set itself changes.
 */
export const useVisibleObjectIds = (): readonly ObjectId[] => {
  const { scene, frames } = useEngine();
  const [ids, setIds] = useState<readonly ObjectId[]>([]);

  useEffect(() => {
    const recompute = () => {
      const { camera, viewport } = useCameraStore.getState();
      if (viewport.size.width === 0) return;
      const next = cullVisible(scene, camera, viewport.size);
      setIds((prev) => (sameSet(prev, next) ? prev : next));
    };
    recompute();
    const unsubCamera = useCameraStore.subscribe(() => {
      frames.schedule(recompute);
    });
    const unsubDoc = useWorkspaceStore.subscribe((s, prev) => {
      if (s.revision !== prev.revision || s.activeWorkspaceId !== prev.activeWorkspaceId) frames.schedule(recompute);
    });
    return () => {
      unsubCamera();
      unsubDoc();
    };
  }, [scene, frames]);

  return ids;
};
